// NodeMissionTask.jsx

import React, { useCallback, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Hooks
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';
import useWorkflowTaskChecklistProgress from 'node/missions/hooks/useWorkflowTaskChecklistProgress';

// Utilities
import {
  getMissionTaskPersonKey,
  getMissionTaskPersonLabel,
  MISSION_TASK_HANDLED_BY,
} from 'missions/taskmodal/missionTaskModalUtils';
import {
  canMarkCompleteMissionTask,
  canNudgeMissionTask,
  canReopenMissionTask,
  canUnblockMissionTask,
  getMissionTaskStatusPresentation,
} from 'node/missions/mission/utils/missionRunUtils';
import {
  formatWorkflowTaskChecklistPercent,
  isWorkflowTaskChecklistBlockingComplete,
  readWorkflowTaskChecklistRecipe,
} from 'node/missions/mission/utils/workflowStepChecklistUtils';
import { normalizeWorkflowOutcomes } from 'workflows/workflowGraphUtils';

// CSS
import './NodeMissionTask.css';

// Components
import Button from 'components/button/Button';
import ProfileStack from 'components/profilestack/ProfileStack';
import NodeMissionTaskAttach from 'node/missions/mission/task/attach/NodeMissionTaskAttach';
import NodeMissionTaskBlocker from 'node/missions/mission/task/blocker/NodeMissionTaskBlocker';
import NodeMissionTaskChecklist from 'node/missions/mission/task/checklist/NodeMissionTaskChecklist';
import NodeMissionTaskHeader from 'node/missions/mission/task/header/NodeMissionTaskHeader';
import NodeMissionTaskAttachments from 'node/missions/mission/task/attachments/NodeMissionTaskAttachments';

/**
 * NodeMissionTask
 *
 * Renders the expanded Mission task details card with checklist, attachments, blocker editor, and actions.
 *
 * @param {object|null} missionRun - Parent Mission run.
 * @param {object} task - Mission task to render.
 * @param {object} template - Mission template for save state comparisons.
 * @param {object|null} user - Current signed-in user.
 * @param {object} missionActions - Shared Mission run action handlers and UI state.
 * @param {object|null} [stepNode] - Linked TASK node for this people step.
 * @param {object|null} [hostNode] - Workflow host record (e.g. PROJECT).
 * @param {function} [onHostNodeChange] - Optional local host patch after field saves.
 * @param {boolean} [compact=false] - Peek density mode.
 * @param {boolean} [bodyExpanded=true] - Whether checklist / actions are shown.
 * @param {function|null} [onToggleBodyExpanded] - Toggle expand when compact + inactive.
 * @returns {ReactNode} The rendered Mission task details card.
 */
const NodeMissionTask = React.memo(({
  missionRun = null,
  task,
  template,
  user = null,
  missionActions,
  stepNode = null,
  hostNode = null,
  onHostNodeChange = null,
  compact = false,
  bodyExpanded = true,
  onToggleBodyExpanded = null,
}) => {
  const { theme } = useTheme();
  const { openNode } = useNodeOpenIntent();
  const [checklistProgress, setChecklistProgress] = useState(null);
  const { percentLabel: subscribedPercentLabel } = useWorkflowTaskChecklistProgress({
    stepNode,
    task,
    template,
  });

  const normalizedTaskKey = String(task?.key || '').trim();
  const stepNodeKey = String(stepNode?.key || task?.nodeKey || '').trim();
  const checklistRecipe = readWorkflowTaskChecklistRecipe(task);
  const showChecklist = Boolean(stepNode?.key) && checklistRecipe.items.length > 0;
  const checklistBlocksComplete = isWorkflowTaskChecklistBlockingComplete(
    task,
    checklistProgress,
  );
  const customOutcomes = useMemo(
    () => normalizeWorkflowOutcomes(task?.outcomes, { handledBy: task?.handledBy }),
    [task?.handledBy, task?.outcomes],
  );

  const assigneeProfiles = useMemo(() => {
    if (task?.handledBy === MISSION_TASK_HANDLED_BY.BOT) return [];

    return (Array.isArray(task?.assignees) ? task.assignees : [])
      .map((assignee) => {
        const key = getMissionTaskPersonKey(assignee);
        if (!key) return null;

        const label = getMissionTaskPersonLabel(assignee) || key;
        const title = String(assignee?.title || '').trim() || label;

        return {
          key,
          userKey: key,
          name: label,
          title,
          coverUrl: assignee?.coverUrl || '',
          data_profile: {
            name: label,
            email: assignee?.email || assignee?.data_profile?.email || '',
          },
        };
      })
      .filter(Boolean);
  }, [task?.assignees, task?.handledBy]);

  const assigneeNameList = useMemo(() => (
    assigneeProfiles
      .map((profile) => profile.title || profile.name)
      .filter(Boolean)
      .join(', ')
  ), [assigneeProfiles]);

  const handleChecklistProgressChange = useCallback((progress) => {
    setChecklistProgress(progress);
  }, []);

  const handleRequestCompleteFromChecklist = useCallback(() => {
    if (!missionRun || !template || !normalizedTaskKey) return;
    if (checklistBlocksComplete) return;
    missionActions?.handleTaskStatusChange?.(
      template,
      missionRun,
      normalizedTaskKey,
      'complete',
    );
  }, [
    checklistBlocksComplete,
    missionActions,
    missionRun,
    normalizedTaskKey,
    template,
  ]);

  if (!missionRun || !template || !normalizedTaskKey) {
    return null;
  }

  const {
    blockingReasonDrafts,
    blockingReasonErrors,
    blockingTaskKey,
    handleBlockingReasonDraftChange,
    handleNudgeTask,
    handleReopenTask,
    handleSaveBlockingReason,
    handleTaskStatusChange,
    handleUnblockTask,
    nudgingTaskKey,
    savingMissionKey,
    setBlockingTaskKey,
  } = missionActions;

  const blockingReasonDraft = blockingReasonDrafts[normalizedTaskKey] ?? String(task?.blockingReason || '');
  const blockingReasonError = blockingReasonErrors[normalizedTaskKey] || '';
  const isBlockingReasonEditorOpen = blockingTaskKey === normalizedTaskKey
    || (task.status === 'blocked' && !String(task?.blockingReason || '').trim());
  const taskStatusPresentation = getMissionTaskStatusPresentation(theme, task);
  const canComplete = canMarkCompleteMissionTask(task);
  const completeEnabled = canComplete
    && savingMissionKey !== template.key
    && !checklistBlocksComplete;
  const hasTaskActions = canComplete
    || canUnblockMissionTask(task)
    || canNudgeMissionTask(task, user)
    || canReopenMissionTask(task)
    || Boolean(stepNodeKey);
  const showFooter = hasTaskActions || assigneeProfiles.length > 0;

  const showBody = bodyExpanded !== false;
  const livePercentLabel = formatWorkflowTaskChecklistPercent(checklistProgress);
  const percentLabel = livePercentLabel || subscribedPercentLabel;

  return (
    <div
      className={[
        'node-mission-task-container',
        compact ? 'is-compact' : '',
        compact && !showBody ? 'is-collapsed' : '',
      ].filter(Boolean).join(' ')}
    >

      <NodeMissionTaskHeader
        bodyExpanded={showBody}
        compact={compact}
        missionRun={missionRun}
        percentLabel={percentLabel}
        task={task}
        taskStatusPresentation={taskStatusPresentation}
        onToggleBodyExpanded={onToggleBodyExpanded}
      />

      {showBody && isBlockingReasonEditorOpen ? (
        <NodeMissionTaskBlocker
          missionRun={missionRun}
          blockingTaskKey={blockingTaskKey}
          blockingReasonError={blockingReasonError}
          blockingReasonDraft={blockingReasonDraft}
          handleBlockingReasonDraftChange={handleBlockingReasonDraftChange}
          handleSaveBlockingReason={handleSaveBlockingReason}
          normalizedTaskKey={normalizedTaskKey}
          setBlockingTaskKey={setBlockingTaskKey}
          savingMissionKey={savingMissionKey}
          template={template}
        />
      ) : null}

      {showBody && showChecklist ? (
        <NodeMissionTaskChecklist
          hostNode={hostNode}
          stepNode={stepNode}
          task={task}
          template={template}
          user={user}
          onHostNodeChange={onHostNodeChange}
          onProgressChange={handleChecklistProgressChange}
          onRequestComplete={canComplete ? handleRequestCompleteFromChecklist : null}
        />
      ) : null}

      {showBody && stepNode?.key ? (
        <NodeMissionTaskAttachments
          stepNode={stepNode}
          user={user}
        />
      ) : null}

      {showBody && showFooter ? (
        <div className="node-mission-task-inline-actions">
          <div className="node-mission-task-primary-actions">
            {canComplete ? (
              <Button
                enabled={completeEnabled}
                iconKey="check"
                label="Complete"
                title={checklistBlocksComplete
                  ? 'Finish the required Task Checklist before completing this step'
                  : undefined}
                onClick={() => handleTaskStatusChange(
                  template,
                  missionRun,
                  normalizedTaskKey,
                  'complete',
                  { completionOutcomeKey: 'complete' },
                )}
              />
            ) : null}

            {canComplete ? customOutcomes.map((outcome) => (
              <Button
                key={outcome.key}
                enabled={completeEnabled}
                iconKey="call_split"
                label={outcome.label}
                title={checklistBlocksComplete
                  ? 'Finish the required Task Checklist before completing this step'
                  : outcome.label}
                onClick={() => handleTaskStatusChange(
                  template,
                  missionRun,
                  normalizedTaskKey,
                  'complete',
                  { completionOutcomeKey: outcome.key },
                )}
              />
            )) : null}

            {canReopenMissionTask(task) ? (
              <Button
                enabled={savingMissionKey !== template.key}
                iconKey="undo"
                label="Reopen"
                onClick={() => handleReopenTask(template, missionRun, normalizedTaskKey)}
              />
            ) : null}

            {canUnblockMissionTask(task) ? (
              <Button
                enabled={savingMissionKey !== template.key}
                fontSize="12px"
                height="34px"
                label="Clear Blocker"
                padding="0 12px"
                onClick={() => handleUnblockTask(template, missionRun, normalizedTaskKey)}
              />
            ) : null}

            {canNudgeMissionTask(task, user) ? (
              <Button
                enabled={savingMissionKey !== template.key && nudgingTaskKey !== normalizedTaskKey}
                fontSize="12px"
                height="34px"
                label={nudgingTaskKey === normalizedTaskKey ? 'Nudging...' : 'Nudge'}
                padding="0 12px"
                onClick={() => handleNudgeTask(template, missionRun, task)}
              />
            ) : null}

            {stepNode ? (
              <NodeMissionTaskAttach
                stepNode={stepNode}
                user={user}
              />
            ) : null}

            {/* 
            {stepNodeKey ? (
              <Button
                backgroundColor="transparent"
                enabled
                fontSize="12px"
                foregroundColor={theme.foregroundMuted}
                height="34px"
                iconKey="open_in_new"
                label="Open task"
                padding="0 12px"
                onClick={() => openNode(stepNode || { key: stepNodeKey }, { preferPeek: true })}
              />
            ) : null}
             */}
          </div>

          {assigneeProfiles.length > 0 ? (
            <div
              className="node-mission-task-assignees"
              title={assigneeNameList}
            >
              <ProfileStack
                allowNavigation={false}
                maxDisplay={4}
                overlap={6}
                profiles={assigneeProfiles}
                size={28}
              />
            </div>
          ) : null}
        </div>
      ) : null}

    </div>
  );
});

NodeMissionTask.displayName = 'NodeMissionTask';

export default NodeMissionTask;
